import { useEffect, useRef, useState, useCallback } from 'react';
import type { Difficulty } from '../../utils/mathProblems';

export interface BallState {
  x: number; z: number; h: number;
  vx: number; vz: number; vh: number;
  bounced: boolean;
  lastHitter: 'player' | 'cpu';
}

interface Props {
  onRallyEnd: (winner: 'player' | 'opponent') => void;
  difficulty: Difficulty;
  isGuest?: boolean;
  externalBall?: BallState | null;
  remoteOpponentX?: number;
  onBallUpdate?: (b: BallState) => void;
  onPaddleUpdate?: (x: number) => void;
  opponentLabel?: string;
}

type Announce = { text: string; color: string; sub?: string } | null;

// ── Canvas constants ─────────────────────────────────────────────────────
const CW = 720;
const CH = 480;
const HORIZON = 172;
const CAM_Y = 0.12;
const FL = 308;
const NEAR = 0.013;
const SX = 0.82;
const NET_Z = 0.5;
const NET_H = 0.19;
const BALL_R = 0.022;
const GRAVITY = 0.009;
const BOUNCE_REST = 0.5;
const HIT_RANGE = 0.28;
const P_SPEED = 0.025;

const CPU_SPD: Record<Difficulty, number> = { easy: 0.013, medium: 0.019, hard: 0.027, expert: 0.037 };
const CPU_ERR: Record<Difficulty, number> = { easy: 0.32, medium: 0.18, hard: 0.07, expert: 0.01 };

// ── First-person projection ──────────────────────────────────────────────
function pj(wx: number, wy: number, wz: number, cx: number, guest: boolean) {
  const dz = guest ? 1.0 - wz : wz;
  const rx = guest ? -(wx - cx) : wx - cx;
  if (dz < NEAR) return null;
  return {
    sx: CW / 2 + (rx / dz) * FL,
    sy: HORIZON + ((CAM_Y - wy) / dz) * FL,
    sc: Math.min(1.4 / dz, 22),
    dz,
  };
}

// ── Ball factory ─────────────────────────────────────────────────────────
function makeShot(fz: number, fx: number, tx: number, hitter: 'player' | 'cpu', err: number): BallState {
  const jit = (Math.random() - 0.5) * err * 1.6;
  const fx2 = Math.max(-0.95, Math.min(0.95, tx + jit));
  const dx = fx2 - fx;
  const dz = hitter === 'player' ? 1 : -1;
  const spd = 0.017 + Math.random() * 0.004;
  return { x: fx, z: fz, h: 0.04, vx: dx * spd * 1.1, vz: dz * spd, vh: 0.13 + Math.random() * 0.04, bounced: false, lastHitter: hitter };
}

// ── Draw court (first-person perspective) ────────────────────────────────
function drawCourt(ctx: CanvasRenderingContext2D, cx: number, guest: boolean) {
  // Sky
  const sky = ctx.createLinearGradient(0, 0, 0, HORIZON);
  sky.addColorStop(0, '#1a5e7d');
  sky.addColorStop(1, '#4da8d4');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, CW, HORIZON);

  // Bleacher rows
  for (let i = 0; i < 7; i++) {
    ctx.fillStyle = `rgba(${155 + i * 7},${115 + i * 6},${65 + i * 3},${0.88 - i * 0.04})`;
    ctx.fillRect(0, HORIZON - 16 - i * 20, CW, 18);
  }

  // Court surface trapezoid
  const farZ = guest ? 0.022 : 1.0;
  const nearZ = guest ? 0.978 : 0.022;
  const fL = pj(-1, 0, farZ, cx, guest);
  const fR = pj(1, 0, farZ, cx, guest);
  const nL = pj(-1, 0, nearZ, cx, guest);
  const nR = pj(1, 0, nearZ, cx, guest);

  if (fL && fR) {
    const cg = ctx.createLinearGradient(0, HORIZON, 0, CH);
    cg.addColorStop(0, '#1a7a3e');
    cg.addColorStop(1, '#25a050');
    ctx.beginPath();
    ctx.moveTo(fL.sx, fL.sy);
    ctx.lineTo(fR.sx, fR.sy);
    ctx.lineTo(nR?.sx ?? (CW + 700), CH + 120);
    ctx.lineTo(nL?.sx ?? -700, CH + 120);
    ctx.closePath();
    ctx.fillStyle = cg;
    ctx.fill();
  }

  // Out zones
  for (const [xl, xr] of [[-1, -SX] as [number, number], [SX, 1] as [number, number]]) {
    const a = pj(xl, 0, farZ, cx, guest);
    const b = pj(xr, 0, farZ, cx, guest);
    const c = pj(xr, 0, nearZ, cx, guest);
    const d = pj(xl, 0, nearZ, cx, guest);
    if (!a || !b) continue;
    ctx.beginPath();
    ctx.moveTo(a.sx, a.sy);
    ctx.lineTo(b.sx, b.sy);
    ctx.lineTo(c?.sx ?? (xr > 0 ? CW + 700 : -700), CH + 120);
    ctx.lineTo(d?.sx ?? (xl > 0 ? CW + 700 : -700), CH + 120);
    ctx.closePath();
    ctx.fillStyle = 'rgba(210,30,30,0.16)';
    ctx.fill();
  }

  // Court lines
  const ln = (x1: number, z1: number, x2: number, z2: number, a = 0.88) => {
    const A = pj(x1, 0, z1, cx, guest);
    const B = pj(x2, 0, z2, cx, guest);
    if (!A || !B) return;
    ctx.save();
    ctx.strokeStyle = `rgba(255,255,255,${a})`;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(A.sx, A.sy);
    ctx.lineTo(B.sx, B.sy);
    ctx.stroke();
    ctx.restore();
  };
  ln(-1, 0, 1, 0); ln(-1, 1, 1, 1);
  ln(-SX, 0, -SX, 1); ln(SX, 0, SX, 1);
  ln(-SX, 0.22, SX, 0.22, 0.52); ln(-SX, 0.78, SX, 0.78, 0.52);
  ln(0, 0.22, 0, 0.78, 0.42);

  // Net
  const nz = NET_Z;
  const nLg = pj(-1, 0, nz, cx, guest);
  const nRg = pj(1, 0, nz, cx, guest);
  const nLt = pj(-1, NET_H, nz, cx, guest);
  const nRt = pj(1, NET_H, nz, cx, guest);
  if (nLg && nRg && nLt && nRt) {
    ctx.fillStyle = 'rgba(200,200,200,0.2)';
    ctx.beginPath();
    ctx.moveTo(nLg.sx, nLg.sy);
    ctx.lineTo(nRg.sx, nRg.sy);
    ctx.lineTo(nRt.sx, nRt.sy);
    ctx.lineTo(nLt.sx, nLt.sy);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(nLt.sx, nLt.sy); ctx.lineTo(nRt.sx, nRt.sy); ctx.stroke();

    for (const nx of [-1, 1]) {
      const pg = pj(nx, 0, nz, cx, guest);
      const pt = pj(nx, NET_H + 0.05, nz, cx, guest);
      if (pg && pt) {
        ctx.strokeStyle = 'rgba(255,255,255,0.7)';
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(pg.sx, pg.sy); ctx.lineTo(pt.sx, pt.sy); ctx.stroke();
      }
    }
    for (let i = 1; i <= 4; i++) {
      const ny = (NET_H * i) / 5;
      const nwL = pj(-1, ny, nz, cx, guest);
      const nwR = pj(1, ny, nz, cx, guest);
      if (nwL && nwR) {
        ctx.strokeStyle = `rgba(255,255,255,${0.08 + i * 0.04})`;
        ctx.lineWidth = 0.7;
        ctx.beginPath(); ctx.moveTo(nwL.sx, nwL.sy); ctx.lineTo(nwR.sx, nwR.sy); ctx.stroke();
      }
    }
  }
}

// ── Draw ball ────────────────────────────────────────────────────────────
function drawBall(ctx: CanvasRenderingContext2D, b: BallState, cx: number, guest: boolean) {
  const p = pj(b.x, b.h, b.z, cx, guest);
  if (!p) return;
  const r = Math.min(BALL_R / p.dz * FL, 68);
  const gs = pj(b.x, 0, b.z, cx, guest);
  if (gs) {
    const sr = r * 0.72;
    const g = ctx.createRadialGradient(gs.sx, gs.sy, 0, gs.sx, gs.sy, sr + 2);
    g.addColorStop(0, `rgba(0,0,0,${Math.max(0, 0.42 - b.h * 0.28)})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(gs.sx, gs.sy, sr, sr * 0.32, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.shadowColor = '#fde047';
  ctx.shadowBlur = Math.min(r * 0.55, 14);
  ctx.fillStyle = '#d4e84a';
  ctx.beginPath();
  ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.beginPath();
  ctx.arc(p.sx - r * 0.28, p.sy - r * 0.3, r * 0.28, 0, Math.PI * 2);
  ctx.fill();
}

// ── Draw opponent player at far end ──────────────────────────────────────
function drawOpponent(ctx: CanvasRenderingContext2D, ox: number, cx: number, guest: boolean, label: string) {
  const oppZ = guest ? 0.03 : 0.97;
  const p = pj(ox, 0, oppZ, cx, guest);
  if (!p) return;
  const pw = 18 * p.sc;
  const ph = 52 * p.sc;
  ctx.fillStyle = '#f97316';
  ctx.beginPath();
  ctx.roundRect(p.sx - pw / 2, p.sy - ph, pw, ph, [3]);
  ctx.fill();
  ctx.fillStyle = '#fcd5a2';
  ctx.beginPath();
  ctx.arc(p.sx, p.sy - ph - 7 * p.sc, 7 * p.sc, 0, Math.PI * 2);
  ctx.fill();
  if (p.sc > 0.4) {
    ctx.font = `bold ${Math.round(9 * p.sc + 5)}px sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.textAlign = 'center';
    ctx.fillText(label, p.sx, p.sy + 12 * p.sc);
  }
}

// ── Draw racket (HUD) ────────────────────────────────────────────────────
function drawRacket(ctx: CanvasRenderingContext2D, swing: number) {
  const baseCx = CW / 2 + 20;
  const baseY = CH + 12;
  ctx.save();
  ctx.translate(baseCx, baseY - 80);
  ctx.rotate(-0.22 + swing * -0.5);
  ctx.strokeStyle = '#5d3a1a';
  ctx.lineWidth = 9;
  ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(-10, -70); ctx.stroke();
  ctx.translate(-10, -98);
  ctx.rotate(0.18);
  ctx.strokeStyle = '#84cc16';
  ctx.lineWidth = 3.5;
  ctx.beginPath();
  ctx.ellipse(0, 0, 28, 36, 0, 0, Math.PI * 2);
  ctx.stroke();
  for (let y = -27; y <= 27; y += 9) {
    const hw = Math.sqrt(Math.max(0, 28 * 28 * (1 - (y / 36) ** 2)));
    ctx.strokeStyle = 'rgba(200,230,80,0.42)';
    ctx.lineWidth = 0.7;
    ctx.beginPath(); ctx.moveTo(-hw, y); ctx.lineTo(hw, y); ctx.stroke();
  }
  for (let x = -24; x <= 24; x += 9) {
    const hh = Math.sqrt(Math.max(0, 36 * 36 * (1 - (x / 28) ** 2)));
    ctx.strokeStyle = 'rgba(200,230,80,0.42)';
    ctx.lineWidth = 0.7;
    ctx.beginPath(); ctx.moveTo(x, -hh); ctx.lineTo(x, hh); ctx.stroke();
  }
  ctx.restore();
}

// ── Main Component ───────────────────────────────────────────────────────
export default function TennisFirstPerson({
  onRallyEnd,
  difficulty,
  isGuest = false,
  externalBall,
  remoteOpponentX,
  onBallUpdate,
  onPaddleUpdate,
  opponentLabel,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [countdown, setCountdown] = useState(3);
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState<'player' | 'opponent' | null>(null);
  const [announce, setAnnounce] = useState<Announce>(null);

  const playerX = useRef(0);
  const cpuX = useRef(0);
  const ball = useRef<BallState | null>(null);
  const keys = useRef<Record<string, boolean>>({});
  const running = useRef(false);
  const done = useRef(false);
  const frame = useRef(0);
  const swingRef = useRef(0);

  // Keep latest callbacks in refs to avoid stale closures
  const onBallUpdateRef = useRef(onBallUpdate);
  const onPaddleUpdateRef = useRef(onPaddleUpdate);
  useEffect(() => { onBallUpdateRef.current = onBallUpdate; }, [onBallUpdate]);
  useEffect(() => { onPaddleUpdateRef.current = onPaddleUpdate; }, [onPaddleUpdate]);

  // Sync remote opponent position into ref
  const remoteXRef = useRef(remoteOpponentX ?? 0);
  useEffect(() => { remoteXRef.current = remoteOpponentX ?? 0; }, [remoteOpponentX]);

  const isOnline = !!onBallUpdate || !!externalBall;
  const cpuSpeed = CPU_SPD[difficulty];
  const cpuErr = CPU_ERR[difficulty];
  const oppLabel = opponentLabel ?? (isGuest ? 'HOST' : isOnline ? 'OPPONENT' : 'CPU');

  const endRally = useCallback((winner: 'player' | 'opponent', ann: Announce) => {
    if (done.current) return;
    done.current = true;
    running.current = false;
    setAnnounce(ann);
    setResult(winner);
  }, []);

  // Countdown
  useEffect(() => {
    if (countdown <= 0) {
      if (!isGuest) {
        const b = makeShot(1, 0, (Math.random() - 0.5) * 0.7, 'cpu', cpuErr * 0.5);
        ball.current = b;
        onBallUpdateRef.current?.(b);
      }
      setStarted(true);
      running.current = true;
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 900);
    return () => clearTimeout(t);
  }, [countdown, cpuErr, isGuest]);

  // Sync external ball (guest only)
  useEffect(() => {
    if (isGuest && externalBall) ball.current = { ...externalBall };
  }, [isGuest, externalBall]);

  // Key listeners
  useEffect(() => {
    const dn = (e: KeyboardEvent) => {
      keys.current[e.key] = true;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) e.preventDefault();
    };
    const up = (e: KeyboardEvent) => { keys.current[e.key] = false; };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', dn); window.removeEventListener('keyup', up); };
  }, []);

  // Game loop
  useEffect(() => {
    if (!started) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let animId: number;

    const loop = () => {
      ctx.clearRect(0, 0, CW, CH);
      drawCourt(ctx, playerX.current, isGuest);

      const b = ball.current;
      if (b) drawBall(ctx, b, playerX.current, isGuest);
      drawOpponent(ctx, cpuX.current, playerX.current, isGuest, oppLabel);
      drawRacket(ctx, swingRef.current);

      // HUD label
      ctx.font = 'bold 10px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.textAlign = 'center';
      ctx.letterSpacing = '2px';
      ctx.fillText('1ST PERSON VIEW', CW / 2, 18);
      ctx.letterSpacing = '0px';

      frame.current++;

      if (running.current && b) {
        const k = keys.current;

        // Controls: guest controls are mirrored (looking from opposite end)
        if (isGuest) {
          if ((k['a'] || k['A'] || k['ArrowLeft']) && playerX.current < 0.88) playerX.current += P_SPEED;
          if ((k['d'] || k['D'] || k['ArrowRight']) && playerX.current > -0.88) playerX.current -= P_SPEED;
        } else {
          if ((k['a'] || k['A'] || k['ArrowLeft']) && playerX.current > -0.88) playerX.current -= P_SPEED;
          if ((k['d'] || k['D'] || k['ArrowRight']) && playerX.current < 0.88) playerX.current += P_SPEED;
        }

        // Broadcast paddle position every 3 frames
        if (frame.current % 3 === 0) onPaddleUpdateRef.current?.(playerX.current);

        if (isGuest) {
          // Guest: sync opponent pos from remote host
          cpuX.current = remoteXRef.current;
        } else {
          // Host/local: run ball physics
          if (isOnline) {
            cpuX.current = remoteXRef.current;
          } else {
            // CPU AI
            if (b.vz > 0 && b.z > 0.3) {
              const diff = b.x - cpuX.current;
              cpuX.current += Math.sign(diff) * Math.min(Math.abs(diff), cpuSpeed);
            }
          }

          b.z += b.vz;
          b.x += b.vx;
          b.h += b.vh;
          b.vh -= GRAVITY;

          // Net check
          const crossNet =
            (b.lastHitter === 'player' && b.z >= NET_Z && b.z - b.vz < NET_Z) ||
            (b.lastHitter === 'cpu' && b.z <= NET_Z && b.z - b.vz > NET_Z);
          if (crossNet && b.h < NET_H) {
            endRally(
              b.lastHitter === 'player' ? 'opponent' : 'player',
              { text: 'NET!', color: '#f97316', sub: b.lastHitter === 'player' ? 'Your shot hit the net!' : 'Opponent hit the net — you win!' }
            );
          }

          // Bounce / landing
          if (b.h <= 0 && !done.current) {
            b.h = 0;
            const out = Math.abs(b.x) > SX;
            const pastPlayer = b.z < 0;
            const pastCpu = b.z > 1;
            if (out) {
              endRally(b.lastHitter === 'player' ? 'opponent' : 'player', { text: 'OUT!', color: '#ef4444', sub: 'Ball landed out!' });
            } else if (pastPlayer) {
              endRally('opponent', { text: 'POINT LOST!', color: '#f97316', sub: 'Ball past your baseline!' });
            } else if (pastCpu) {
              endRally('player', { text: 'WINNER!', color: '#84cc16', sub: "Ball past opponent's baseline!" });
            } else if (!b.bounced) {
              b.bounced = true;
              b.vh = Math.abs(b.vh) * BOUNCE_REST;
              b.vx *= 0.82;
            } else {
              if (b.vz < 0 && b.z < 0.5) endRally('opponent', { text: 'POINT LOST!', color: '#f97316', sub: 'Ball bounced twice — you lose the rally!' });
              else if (b.vz > 0 && b.z > 0.5) endRally('player', { text: 'WINNER!', color: '#84cc16', sub: "Ball bounced twice on opponent's side!" });
              else b.vh = Math.abs(b.vh) * BOUNCE_REST * 0.4;
            }
          }

          // Player (host) hit zone — ball at z≈0
          if (!done.current && b.vz < 0 && b.z <= 0.04 && b.z >= -0.06 && b.lastHitter === 'cpu') {
            const dist = Math.abs(b.x - playerX.current);
            if (dist <= HIT_RANGE) {
              const aim = playerX.current < b.x ? b.x - 0.3 : b.x + 0.3;
              ball.current = makeShot(0, b.x, Math.max(-0.9, Math.min(0.9, aim + (Math.random() - 0.5) * 0.1)), 'player', 0.06);
              swingRef.current = 1;
              setTimeout(() => { swingRef.current = 0; }, 210);
            } else {
              endRally('opponent', { text: 'MISS!', color: '#f97316', sub: "You didn't reach the ball in time!" });
            }
          }

          // Opponent (cpu/guest) hit zone — ball at z≈1
          if (!done.current && b.vz > 0 && b.z >= 0.96 && b.z <= 1.06 && b.lastHitter === 'player') {
            const dist = Math.abs(b.x - cpuX.current);
            const range = isOnline ? HIT_RANGE : HIT_RANGE * (1.1 - cpuErr);
            if (dist <= range) {
              const aim = isOnline ? cpuX.current + (Math.random() - 0.5) * 0.4 : (Math.random() - 0.5) * 1.1;
              ball.current = makeShot(1, b.x, aim, 'cpu', isOnline ? 0.05 : cpuErr);
            } else {
              endRally('player', { text: 'WINNER!', color: '#84cc16', sub: "Opponent didn't reach it!" });
            }
          }

          // Broadcast ball state every 2 frames (host)
          if (frame.current % 2 === 0 && ball.current) onBallUpdateRef.current?.(ball.current);
        }
      }

      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, [started, cpuSpeed, cpuErr, endRally, isGuest, isOnline, oppLabel]);

  // Auto-proceed after result
  useEffect(() => {
    if (!result) return;
    const delay = isOnline ? 1400 : 2500;
    const t = setTimeout(() => onRallyEnd(result), delay);
    return () => clearTimeout(t);
  }, [result, onRallyEnd, isOnline]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-center">
        <p className="text-sky-700 font-semibold text-sm">
          <i className="ri-eye-line mr-1 text-sky-500" />
          First-Person View — Position yourself and return the shot!
        </p>
      </div>

      <div className="relative rounded-2xl overflow-hidden border-2 border-sky-700" style={{ width: CW, height: CH }}>
        <canvas ref={canvasRef} width={CW} height={CH} style={{ display: 'block' }} />

        {!started && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55">
            <div className="font-bold text-white leading-none"
              style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: countdown === 0 ? '90px' : '120px', letterSpacing: '0.05em' }}>
              {countdown === 0 ? 'GO!' : countdown}
            </div>
            <p className="text-sky-300 text-sm mt-3 font-medium">
              {isGuest ? 'A ← / D → (controls are mirrored — you\'re at the far end!)' : 'Use A/D or ←/→ to move and intercept the ball'}
            </p>
          </div>
        )}

        {announce && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
            <div className="font-bold leading-none drop-shadow-xl"
              style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '88px', letterSpacing: '0.08em', color: announce.color }}>
              {announce.text}
            </div>
            {announce.sub && <p className="text-white text-base mt-3 font-medium text-center max-w-xs">{announce.sub}</p>}
            {result && (
              <div className="mt-4 px-5 py-1.5 rounded-full font-bold"
                style={{ background: result === 'player' ? '#84cc16' : '#f97316', color: '#fff', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em', fontSize: '20px' }}>
                {result === 'player' ? '✦ YOU WIN THE RALLY ✦' : '✦ OPPONENT WINS THE RALLY ✦'}
              </div>
            )}
            <p className="text-gray-400 text-xs mt-3">Continuing in a moment…</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="font-medium">Move:</span>
        {['A', 'D'].map(k => (
          <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
        ))}
        <span>or</span>
        {['←', '→'].map(k => (
          <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
        ))}
        {isGuest && <span className="text-orange-500 font-medium ml-1">(controls mirrored)</span>}
        <span className="text-red-400 font-medium ml-2"><i className="ri-alert-line mr-1" />Red zones = OUT</span>
      </div>
    </div>
  );
}
